import _DebriefViewStatusSubscription from "../__generated__/DebriefViewStatusSubscription.graphql";
import _DebriefViewQuery from "../__generated__/DebriefViewQuery.graphql";
import { Suspense, useCallback, useMemo, useState } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useRelayEnvironment, useSubscription } from 'react-relay';
import { LoadingState } from '../components/LoadingState';
import { formatDateTime } from '../i18n/format';
import { t } from '../i18n/t';
import { En } from '../i18n/En';
import { debriefExpected, debriefState, practisedRows, type PractisedRow } from './debrief.ts';
import { sceneParts } from './topics.ts';
import { Check, Circle } from 'lucide-react';
import { FocusAreaWidget } from './FocusAreaWidget';
import { GeneratedText } from './GeneratedText';
import type { DebriefViewQuery } from '../__generated__/DebriefViewQuery.graphql';
import type { DebriefViewStatusSubscription, DebriefViewStatusSubscription$data } from '../__generated__/DebriefViewStatusSubscription.graphql';
import type { PracticeOutcome } from './PracticeStatsRow';
const DebriefViewQuery = (_DebriefViewQuery.hash && _DebriefViewQuery.hash !== "4ea1ab0e76dfc6d9e40b0b5c98b7286b" && console.error("The definition of 'DebriefViewQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _DebriefViewQuery);
const DebriefStatus = (_DebriefViewStatusSubscription.hash && _DebriefViewStatusSubscription.hash !== "51afa81acd7b3f113b73c5ed0bbf1502" && console.error("The definition of 'DebriefViewStatusSubscription' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _DebriefViewStatusSubscription);
type Debrief = NonNullable<DebriefViewStatusSubscription$data['debriefStatus']>;

/**
 * Nothing is shown until the session ends. The subscription behind this is
 * live from mount — it has to be, or `endSession`'s enqueue publish arrives
 * before anyone is listening — but a waiting message under the talk button
 * while the learner is still speaking is spec §9's "one view, reached when a
 * session ends" broken.
 */
/** Targeted-practice §9. Nothing at all for a session that targeted nothing. */
function Practised({
  rows
}: {
  rows: readonly PractisedRow[];
}) {
  if (rows.length === 0) return null;
  return <div className="mb-6">
      <h3 className="font-semibold text-lg mb-2">{t('debrief-practised')}</h3>
      <ul className="space-y-3">
        {rows.map(r => <li key={r.id} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">{r.label}</span>
              <span className="badge badge-ghost shrink-0">{r.outcome}</span>
            </div>
            {r.learnerForm && r.targetForm && <p className="text-base-content/70 text-sm">
                <En>
                  <s>{r.learnerForm}</s> → {r.targetForm}
                </En>
              </p>}
          </li>)}
      </ul>
    </div>;
}

/**
 * Adult role-play design §2 and its amendment: whether a scene's goal was
 * reached, above the summary, and which of its parts the learner did.
 */
function SceneGoal({
  goal,
  labels
}: {
  goal: NonNullable<Debrief['goal']>;
  labels: string[] | null;
}) {
  return <div className="mb-6 flex flex-col gap-3 rounded-box border border-base-300 bg-base-100 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-lg">{t('debrief-goal')}</h3>
        <span className={`badge ${goal.reached ? 'badge-success' : 'badge-warning'}`}>
          {t(goal.reached ? 'debrief-goal-reached' : 'debrief-goal-not-yet')}
        </span>
      </div>
      {labels && labels.length === goal.parts.length && <ul className="flex flex-col gap-1">
          {goal.parts.map((done, i) => <li key={i} className={`flex items-center gap-2 ${done ? '' : 'text-base-content/60'}`}>
              {done ? <Check aria-hidden className="size-4 shrink-0 text-success" /> : <Circle aria-hidden className="size-4 shrink-0" />}
              <span>{labels[i]}</span>
              <span className="sr-only">{t(done ? 'debrief-goal-part-done' : 'debrief-goal-part-missed')}</span>
            </li>)}
        </ul>}
    </div>;
}
function Debriefing({
  debrief,
  ended,
  hasTurns,
  outcomeMap,
  practised,
  topic
}: {
  debrief: Debrief | null;
  ended: boolean;
  hasTurns: boolean;
  outcomeMap: Map<string, PracticeOutcome[]>;
  practised: readonly PractisedRow[];
  topic: string | null | undefined;
}) {
  const state = debriefState(debrief, hasTurns);
  if (!ended && (state === 'none' || state === 'waiting' || state === 'skipped')) return null;
  switch (state) {
    // `none` here is the gap between `endSession` returning and its publish
    // arriving, which the learner cannot tell from PENDING and need not.
    case 'none':
    case 'waiting':
      return <LoadingState />;
    // Not an error, and not a wait. The server queues no debrief for a session
    // with no turns in it, so this is the end state for one -- said plainly,
    // because a spinner that never resolves is the alternative.
    case 'skipped':
      return <p className="text-base-content/60 py-4 text-sm">{t('debrief-too-short')}</p>;
    case 'failed':
      return <p role="alert">{t('debrief-failed')}</p>;
    case 'ready':
      {
        const d = debrief!;
        return <div className="card bg-base-200 card-body p-4 sm:p-6">
          <h2 className="card-title text-2xl mb-4">{t('debrief-title')}</h2>
          <Practised rows={practised} />
          {d.goal && <SceneGoal goal={d.goal} labels={topic ? sceneParts(topic) : null} />}
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-2">{t('debrief-summary')}</h3>
            <p className="text-base-content">
              <GeneratedText language={d.language}>{d.summary}</GeneratedText>
            </p>
          </div>
          {d.strengths.length > 0 && <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">{t('debrief-strengths')}</h3>
              <ul className="list-disc ps-5 space-y-1">
                {d.strengths.map((s, i) => <li key={i}>
                    <GeneratedText language={d.language}>{s}</GeneratedText>
                  </li>)}
              </ul>
            </div>}
          {d.focusAreas.length > 0 && <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">{t('debrief-focus-areas')}</h3>
              <ol className="space-y-3">
                {d.focusAreas.map((area, i) => <FocusAreaWidget key={i} area={area} language={d.language} outcomeMap={outcomeMap} />)}
              </ol>
            </div>}
          {/* Which model wrote this, and when. Not a detail while more
            than one model can produce a debrief — spec §12 records that the
            27B has never completed a call on this hardware. */}
          <div className="divider my-2">
            <small className="text-base-content/60">
              <bdi>{d.model}</bdi>
              {d.generatedAt && <>
                  , <bdi>{formatDateTime(new Date(d.generatedAt as string))}</bdi>
                </>}
            </small>
          </div>
        </div>;
      }
  }
}
function buildOutcomeMap(practice: NonNullable<NonNullable<DebriefViewQuery['response']>['session']>['practice'] | null | undefined): Map<string, PracticeOutcome[]> {
  const map = new Map<string, PracticeOutcome[]>();
  if (!practice) return map;
  for (const {
    item,
    outcome
  } of practice) {
    const key = item.kind;
    let list = map.get(key);
    if (!list) {
      list = [];
      map.set(key, list);
    }
    list.push({
      item,
      outcome
    } as PracticeOutcome);
  }
  return map;
}
function DebriefLive({
  sessionId,
  ended,
  hasTurns
}: {
  sessionId: string;
  ended: boolean;
  hasTurns?: boolean;
}) {
  const data = useLazyLoadQuery<DebriefViewQuery>(DebriefViewQuery, {
    sessionId
  },
  // Never the store's copy: other operations write `Session.status` into it
  // (endSession, the progress list) without the debrief link, and history
  // decides "too short" from the pair. The fallback is `null`, so a fetch
  // shows nothing rather than a flash.
  {
    fetchPolicy: 'network-only'
  });
  const settled = debriefExpected(hasTurns, data.session);
  // Derived, not copied into state once: the query's debrief is the floor,
  // and the subscription's latest word replaces it.
  const [live, setLive] = useState<Debrief | null>(null);
  const debrief = live ?? data.session?.debrief ?? null;
  const outcomeMap = useMemo(() => buildOutcomeMap(data.session?.practice), [data.session?.practice]);
  const practised = useMemo(() => practisedRows(data.session?.practice, data.session?.practisedWord), [data.session?.practice, data.session?.practisedWord]);
  const environment = useRelayEnvironment();
  const onNext = useCallback((next: DebriefViewStatusSubscription$data | null | undefined) => {
    if (!next) return;
    setLive(next.debriefStatus);
    // The worker resolves `practice` and `practisedWord` in the transaction
    // that completes the debrief, so the outcomes this screen mounted with
    // are stale by the time DONE arrives: the word-practice hand run showed
    // "Didn't come up" beside a server that said USED_CORRECTLY. Refetched
    // into the store, which the query above re-reads.
    if (next.debriefStatus?.status === 'DONE') {
      fetchQuery<DebriefViewQuery>(environment, DebriefViewQuery, {
        sessionId
      }).subscribe({});
    }
  }, [environment, sessionId]);

  // Memoised for the same reason `SessionView`'s `Events` config is: an
  // inline object would re-subscribe on every render, including the ones
  // this subscription's own updates cause.
  const config = useMemo(() => ({
    subscription: DebriefStatus,
    variables: {
      sessionId
    },
    onNext
  }), [sessionId, onNext]);
  useSubscription<DebriefViewStatusSubscription>(config);
  return <Debriefing debrief={debrief} ended={ended} hasTurns={settled} outcomeMap={outcomeMap} practised={practised} topic={data.session?.topic} />;
}
export function DebriefView({
  sessionId,
  ended,
  hasTurns
}: {
  sessionId: string;
  ended: boolean;
  /**
   * Whether the session recorded any learner turn — see `debriefState`.
   *
   * The live screen knows it and passes it, and is trusted over its store
   * (`debriefExpected`). History has no count and omits it, so the session's
   * own status answers instead.
   */
  hasTurns?: boolean;
}) {
  return (
    // `null`, not a message: this mounts at the start of the session, so the
    // fallback would otherwise flash "Reviewing the session…" under the talk
    // button while the learner is starting to speak.
    <Suspense fallback={null}>
      <DebriefLive sessionId={sessionId} ended={ended} hasTurns={hasTurns} />
    </Suspense>
  );
}